import React, { useState, useEffect } from 'react';
import { tokens as tk } from './theme';
import { UsersRound, BadgeDollarSign, Clock3, RefreshCcw, ClipboardCheck, Sparkles, TrendingUp, FileCheck2, PhoneCall, ChevronDown, ArrowRight } from 'lucide-react';

const API = 'https://tcio-api.onrender.com';

const DOT = '\u00B7';
const DASH = '\u2014';
const ELLIP = '\u2026';
const UP = '\u25B2';
const DOWN = '\u25BC';
const STAR = '\u2726';
const CHECK = '\u2713';

const CSS = `
.tcRow { transition: background 160ms ease; }
.tcRow:hover { background: ${tk.surfaceSoft}; }
.tcRowOpen { background: ${tk.aiSoft} !important; }
.tcBtn { transition: border-color 160ms ease, background 160ms ease, color 160ms ease; }
.tcBtn:hover { border-color: ${tk.blue400}; color: ${tk.blue700}; }
.tcChev { transition: transform 180ms ease; }
.tcChevOpen { transform: rotate(180deg); }
.tcFix { transition: color 160ms ease; }
.tcFix:hover { color: ${tk.aiHover}; }
`;

const money = (n) => n ? '$' + Math.round(n).toLocaleString() : DASH;
const pct = (n) => n == null ? DASH : n.toFixed(0) + '%';
const scoreColor = (v) => v == null ? tk.textMuted : v >= 85 ? tk.success : v >= 65 ? tk.warning : tk.danger;
const initials = (name) => (name || '').split(' ').map(w => w[0] || '').join('').slice(0, 2).toUpperCase();
const AV = [tk.blue600, tk.purple, tk.teal, tk.ai, tk.blue700, tk.success];
const avColor = (name) => AV[(((name || '').charCodeAt(0) || 0) + ((name || '').charCodeAt(1) || 0)) % AV.length];

const compAccent = (c) => {
  const k = ((c.key || '') + ' ' + (c.label || '')).toLowerCase();
  if (k.includes('admit')) return tk.blue600;
  if (k.includes('quality') || k.includes('coach') || k.includes('retention') || k.includes('ama')) return tk.purple;
  if (k.includes('accuracy') || k.includes('data') || k.includes('speed') || k.includes('sheet')) return tk.teal;
  if (k.includes('follow')) return tk.warning;
  if (k.includes('vob')) return tk.blue700;
  return tk.blue600;
};
const compValueColor = (c) => {
  if (!c.has_data) return tk.textMuted;
  const r = c.pts / c.max;
  if (r >= 1) return tk.success;
  if (r >= 0.8) return tk.success;
  if (r >= 0.5) return tk.warning;
  return tk.danger;
};

export default function Scorecard({ user, nav }) {
  const [d, setD] = useState(null);
  const [act, setAct] = useState(null);
  const [days, setDays] = useState(7);
  const [showAll, setShowAll] = useState(true);
  const [ts, setTs] = useState(null);
  const [expand, setExpand] = useState(null);
  const [howOpen, setHowOpen] = useState(false);
  const [vobOpen, setVobOpen] = useState(null);
  const [isNarrow, setIsNarrow] = useState(typeof window !== 'undefined' && window.innerWidth < 1000);

  const headers = { Authorization: 'Bearer ' + user.token };
  const isAdmin = ['ceo', 'admin'].includes(user.role);

  useEffect(() => {
    const onR = () => setIsNarrow(window.innerWidth < 1000);
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);

  useEffect(() => {
    fetch(API + '/api/scorecard/activity?days=' + days, { headers })
      .then(r => r.json()).then(j => j.ok && setAct(j)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  useEffect(() => {
    const q = isAdmin && showAll ? '?all=true' : '';
    fetch(API + '/api/scorecard' + q, { headers })
      .then(r => r.json()).then(j => j.ok && setD(j)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAll]);

  useEffect(() => {
    fetch(API + '/api/scorecard/tcio-score', { headers })
      .then(r => r.json()).then(j => j.ok && setTs(j)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!d) return <div style={s.wrap}><div style={s.empty}>Loading{ELLIP}</div></div>;

  const rows = d.data.filter(r => r.admits > 0);
  const perfById = {}; d.data.forEach(r => { perfById[r.id] = r; });
  const actById = {}; if (act) act.data.forEach(r => { actById[r.id] = r; });
  const vobById = {}; (d.vob_docs || []).forEach(v => { vobById[v.id] = v; });

  const weights = (ts && ts.data && ts.data[0] && ts.data[0].components) ? ts.data[0].components.map(c => ({ label: c.label, max: c.max })) : [];
  const totalCollected = (isAdmin && d.revenue) ? d.revenue.reduce((n, r) => n + (parseFloat(r.collected) || 0), 0) : null;
  const goalPct = (ts && ts.team_goal > 0) ? Math.round(ts.team_admits / ts.team_goal * 100) : null;

  const dcRows = rows.filter(r => r.discharged > 0 && r.avg_los != null);
  const teamLos = dcRows.length ? (dcRows.reduce((n, r) => n + parseFloat(r.avg_los) * r.discharged, 0) / dcRows.reduce((n, r) => n + r.discharged, 0)) : null;
  const teamDischarged = rows.reduce((n, r) => n + r.discharged, 0);
  const teamAma = rows.reduce((n, r) => n + r.ama_aca, 0);
  const teamAmaRate = teamDischarged > 0 ? Math.round(teamAma / teamDischarged * 100) : null;
  const sheetRows = act ? act.data.filter(r => r.completeness_pct != null && r.open_leads > 0) : [];
  const teamSheet = sheetRows.length ? Math.round(sheetRows.reduce((n, r) => n + r.completeness_pct * r.open_leads, 0) / sheetRows.reduce((n, r) => n + r.open_leads, 0)) : null;

  const overdueTotal = act ? act.data.reduce((n, r) => n + (r.overdue_now || 0), 0) : 0;
  const vobMissingTotal = (d.vob_docs || []).reduce((n, v) => n + (v.missing ? v.missing.length : 0), 0);
  const reviewPending = act ? (act.review_pending || 0) : 0;
  const protectItems = [];
  if (overdueTotal > 0) protectItems.push({ n: overdueTotal, label: 'overdue lead' + (overdueTotal > 1 ? 's' : ''), action: 'Fix now', go: () => nav && nav('queue') });
  if (vobMissingTotal > 0) protectItems.push({ n: vobMissingTotal, label: 'VOB PDF' + (vobMissingTotal > 1 ? 's' : '') + ' missing', action: null });
  if (reviewPending > 0) protectItems.push({ n: reviewPending, label: 'AI call' + (reviewPending > 1 ? 's' : '') + ' awaiting review', action: null });

  const monthLabel = ts ? new Date(ts.month + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '';
  const jumpQueue = (repId) => { localStorage.setItem('tcio_queue_rep', repId); if (nav) nav('queue'); };

  const kpis = [];
  if (ts) kpis.push({ I: UsersRound, tint: tk.aiSoft, ic: tk.blue600, num: ts.team_admits + ' / ' + ts.team_goal, label: 'Team Admits', sub: goalPct != null ? goalPct + '% of goal' + (ts.team_admits >= ts.team_goal ? ' ' + CHECK : '') : '', sc: ts.team_admits >= ts.team_goal ? tk.success : tk.textSecondary });
  if (totalCollected != null) kpis.push({ I: BadgeDollarSign, tint: tk.successBg, ic: tk.success, num: money(totalCollected), label: 'Collected Revenue', sub: 'all-time ' + DOT + ' admin only', sc: tk.textMuted });
  if (teamLos != null) kpis.push({ I: Clock3, tint: tk.tealSoft, ic: tk.teal, num: teamLos.toFixed(1), label: 'Avg LOS (days)', sub: teamDischarged + ' discharges', sc: tk.textMuted });
  if (teamAmaRate != null) kpis.push({ I: RefreshCcw, tint: tk.purpleSoft, ic: tk.purple, num: teamAmaRate + '%', label: 'AMA / ACA Rate', sub: teamAma + ' of ' + teamDischarged, sc: teamAmaRate > 20 ? tk.danger : tk.textMuted });
  if (teamSheet != null) kpis.push({ I: ClipboardCheck, tint: tk.tealSoft, ic: tk.teal, num: teamSheet + '%', label: 'Lead Sheet Complete', sub: 'open leads, team avg', sc: teamSheet >= 80 ? tk.success : teamSheet < 50 ? tk.danger : tk.textMuted });
  if (isAdmin && ts && ts.bonus_summary) kpis.push({ I: TrendingUp, tint: tk.successBg, ic: tk.success, num: '$' + ts.bonus_summary.total_payout.toLocaleString(), label: 'Projected Payout', sub: 'tier \u00D7' + ts.bonus_summary.tier_mult + (ts.bonus_summary.stretch_active ? ' + stretch' : ''), sc: tk.textMuted });

  const trendCell = (r) => {
    if (r.mom_delta == null) return <span style={{ color: tk.textMuted, fontSize: 13 }}>{DASH}</span>;
    const up = r.mom_delta > 0;
    const zero = r.mom_delta === 0;
    const base = r.last_month ? parseFloat(r.last_month.score) : null;
    const p = base ? Math.abs(r.mom_delta / base * 100).toFixed(1) : null;
    return (
      <span style={{ color: zero ? tk.textMuted : up ? tk.success : tk.danger, fontWeight: 600, fontSize: 13 }}>
        {zero ? DASH + ' 0.0' : (up ? UP + ' +' : DOWN + ' ') + r.mom_delta + (p ? ' (' + p + '%)' : '')}
      </span>
    );
  };

  const Breakdown = (r) => {
    const a = actById[r.rep_id];
    const v = vobById[r.rep_id];
    return (
      <div style={{ padding: '2px 4px 14px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isNarrow ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8 }}>
          {r.components.map(c => (
            <div key={c.key} style={{ ...s.compCell, borderLeft: '2px solid ' + compAccent(c) }}>
              <div style={s.compLabel}>{c.label}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: compValueColor(c), letterSpacing: '-0.01em' }}>
                {c.has_data ? c.pts + ' / ' + c.max : 'no data'}
              </div>
            </div>
          ))}
        </div>
        {(a && a.overdue_now > 0) || (v && v.missing && v.missing.length > 0) ? (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 10 }}>
            {a && a.overdue_now > 0 && (
              <button onClick={() => jumpQueue(r.rep_id)} className="tcFix" style={s.fixLink}>
                {a.overdue_now} overdue lead{a.overdue_now > 1 ? 's' : ''} {DASH} open queue <ArrowRight size={12} style={{ verticalAlign: '-1px' }} />
              </button>
            )}
            {v && v.missing && v.missing.length > 0 && (
              <span style={{ fontSize: 12, fontWeight: 600, color: tk.warning }}>
                {v.missing.length} VOB PDF{v.missing.length > 1 ? 's' : ''} missing
              </span>
            )}
          </div>
        ) : null}
        {isAdmin && r.bonus && (
          <div style={{ fontSize: 12, color: tk.textSecondary, marginTop: 10 }}>
            Bonus: <span style={{ fontWeight: 700, color: r.bonus.total > 0 ? tk.success : tk.textMuted }}>{r.bonus.total > 0 ? '$' + r.bonus.total.toLocaleString() : DASH}</span>
            {r.bonus.stretch > 0 ? ' (incl $' + r.bonus.stretch.toLocaleString() + ' stretch)' : ''}
            <span style={{ color: tk.textMuted, marginLeft: 8 }}>{r.bonus.math || ''}</span>
          </div>
        )}
      </div>
    );
  };

  const panelGrid = { display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : '1.4fr 1fr 1fr', gap: 14, alignItems: 'start', marginBottom: 14 };

  return (
    <div style={s.wrap}>
      <style>{CSS}</style>
      <div style={{ ...s.head, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={s.h1}>{isAdmin && showAll ? 'Team Scorecard' : 'My Scorecard'}</h1>
          <p style={s.sub}>{d.note}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {monthLabel && <span style={s.monthChip}>{monthLabel}</span>}
          {isAdmin && (
            <select value={showAll ? 'team' : 'me'} onChange={e => setShowAll(e.target.value === 'team')} style={s.select}>
              <option value="team">Whole Team</option>
              <option value="me">Just Me</option>
            </select>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isNarrow ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(172px, 1fr))', gap: 12, marginBottom: 16 }}>
        {kpis.map((t, i) => {
          const I = t.I;
          return (
            <div key={i} style={s.kpi}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <I size={18} color={t.ic} strokeWidth={1.75} />
                <span style={s.kpiLabel}>{t.label}</span>
              </div>
              <span style={s.kpiNum}>{t.num}</span>
              {t.sub && <span style={{ fontSize: 11.5, fontWeight: 600, color: t.sc }}>{t.sub}</span>}
            </div>
          );
        })}
      </div>

      {protectItems.length > 0 && (
        <div style={s.protect}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Sparkles size={16} color={tk.purple} strokeWidth={1.75} />
            <span style={{ fontSize: 12, fontWeight: 700, color: tk.ink, letterSpacing: '0.05em' }}>PROTECT YOUR SCORE</span>
            <span style={{ fontSize: 11.5, color: tk.textMuted }}>fix these before the score locks on the 1st</span>
          </div>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
            {protectItems.map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: tk.ink }}>{p.n}</span>
                <span style={{ fontSize: 12.5, color: tk.textSecondary }}>{p.label}</span>
                {p.action && (
                  <button onClick={p.go} className="tcFix" style={s.fixLink}>{p.action} <ArrowRight size={11} style={{ verticalAlign: '-1px' }} /></button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {ts && (
        <div style={{ ...s.card, padding: 0, overflow: 'hidden' }}>
          <div onClick={() => setHowOpen(!howOpen)} style={s.howBar}>
            <span style={{ fontSize: 12, fontWeight: 700, color: tk.ink, letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>HOW YOU'RE SCORED (0{'\u2013'}100)</span>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              {weights.map(w => (
                <span key={w.label} style={{ fontSize: 11.5, color: tk.textSecondary, whiteSpace: 'nowrap' }}>{w.label} <b style={{ color: tk.ink }}>{w.max}</b></span>
              ))}
              <ChevronDown size={15} color={tk.textMuted} className={'tcChev' + (howOpen ? ' tcChevOpen' : '')} />
            </div>
          </div>
          {howOpen && (
            <div style={s.howBody}>
              <b>HOW YOU'RE SCORED (0{'\u2013'}100, monthly):</b> Admits vs goal of 18 {DASH} marketing-sourced count 1.5{'\u00D7'}, and every admit past goal adds bonus points, so big months can carry you to 100 (28+ pts) {DOT} Retention {DASH} AMA/early drops count against you (12) {DOT} <b>Call discipline {DASH} every logged call is verified against phone records automatically; outcomes logged without a tracked Call-button call are flagged and dinged hard (10)</b> {DOT} Lead sheet complete (10) {DOT} Data accuracy {DASH} your lead form vs the VOB document vs Kipu, checked by AI on every upload (10) {DOT} Call quality {DASH} AI coaching rubric on qualified calls: greeting, got the name, discovery, next step secured (8) {DOT} VOB PDFs attached (7) {DOT} Speed to first dial (8) {DOT} Follow-up {DASH} overdue leads hurt (7). Team hitting {ts.team_goal} admits adds +3 to everyone. Your monthly score locks on the 1st.
            </div>
          )}
        </div>
      )}

      {ts && (
        <div style={s.card}>
          <div style={s.cardTitle}>TCIO SCORE {DASH} {monthLabel}</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead><tr>{['Rep', 'Score', 'vs Last Month', 'All-Time', 'Admits (wtd)', 'Avg LOS', 'AMA/ACA', 'Sheet %', ...(ts.bonus_summary ? ['Bonus'] : []), ''].map((h, i) => <th key={i} style={s.th}>{h}</th>)}</tr></thead>
              <tbody>
                {[...ts.data].sort((a, b) => ((b.score != null ? b.score : -1) - (a.score != null ? a.score : -1))).map(r => {
                  const p = perfById[r.rep_id];
                  const a = actById[r.rep_id];
                  const open = expand === r.rep_id;
                  return (
                    <React.Fragment key={r.rep_id}>
                      <tr onClick={() => setExpand(open ? null : r.rep_id)} className={'tcRow' + (open ? ' tcRowOpen' : '')} style={{ cursor: 'pointer', borderTop: '1px solid ' + tk.borderSoft }}>
                        <td style={{ ...s.td, fontWeight: 600 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ ...s.avatar, background: avColor(r.rep) }}>{initials(r.rep)}</span>
                            {r.rep}
                          </div>
                        </td>
                        <td style={{ ...s.td, fontWeight: 700, fontSize: 22, color: scoreColor(r.score), letterSpacing: '-0.02em' }}>{r.score != null ? r.score : DASH}</td>
                        <td style={s.td}>{trendCell(r)}</td>
                        <td style={{ ...s.td, fontWeight: 600 }}>{r.all_time != null ? r.all_time : DASH}</td>
                        <td style={s.td}>{r.admits}{r.marketing_admits > 0 ? ' (' + r.weighted_admits + ' wtd)' : ''}</td>
                        <td style={s.td}>{p && p.avg_los ? p.avg_los + 'd' : DASH}</td>
                        <td style={{ ...s.td, color: p && p.ama_aca > 0 ? tk.danger : tk.textMuted }}>{p ? p.ama_aca : DASH}</td>
                        <td style={{ ...s.td, fontWeight: 600, color: a && a.completeness_pct != null ? (a.completeness_pct < 50 ? tk.danger : a.completeness_pct >= 80 ? tk.success : tk.textSecondary) : tk.textMuted }}>{a && a.completeness_pct != null ? a.completeness_pct + '%' : DASH}</td>
                        {ts.bonus_summary && (
                          <td style={{ ...s.td, fontWeight: 700, color: r.bonus && r.bonus.total > 0 ? tk.success : tk.textMuted }} title={r.bonus ? r.bonus.math || '' : ''}>
                            {r.bonus && r.bonus.total > 0 ? '$' + r.bonus.total.toLocaleString() : DASH}
                          </td>
                        )}
                        <td style={s.td}>
                          <ChevronDown size={15} color={tk.textMuted} className={'tcChev' + (open ? ' tcChevOpen' : '')} />
                        </td>
                      </tr>
                      {open && (
                        <tr><td colSpan={ts.bonus_summary ? 10 : 9} style={{ padding: '0 10px', background: tk.aiSoft }}>{Breakdown(r)}</td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {ts.bonus_summary && (
            <div style={{ fontSize: 12.5, fontWeight: 600, color: tk.textSecondary, marginTop: 10 }}>
              Team tier {'\u00D7'}{ts.bonus_summary.tier_mult}{ts.bonus_summary.stretch_active ? ' + $4K stretch pool active' : ''} {DASH} total projected payout: <span style={{ color: tk.ink, fontWeight: 700 }}>${ts.bonus_summary.total_payout.toLocaleString()}</span>
            </div>
          )}
        </div>
      )}

      {rows.length === 0 && (
        <div style={{ ...s.card, background: tk.surfaceSoft, textAlign: 'center', color: tk.textMuted }}>
          Discharge & LOS metrics start once patients are admitted through TCIO.
        </div>
      )}

      <div style={panelGrid}>
        {act && (
          <div style={{ ...s.card, marginBottom: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <div style={{ ...s.cardTitle, display: 'flex', alignItems: 'center', gap: 7 }}><PhoneCall size={14} color={tk.blue600} strokeWidth={1.75} /> ACTIVITY & ACCOUNTABILITY</div>
              <select value={days} onChange={e => setDays(+e.target.value)} style={{ ...s.select, padding: '5px 8px', fontSize: 12 }}>
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
              </select>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={s.table}>
                <thead>
                  <tr>{['Rep', 'Open', 'Overdue', 'Oldest', 'Sheet %', 'Logged'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {act.data.filter(r =>
                    (r.open_leads > 0 || r.outcomes_period > 0 || r.calls_answered > 0 || r.reviews_resolved > 0)
                  ).map(r => {
                    const bad = r.overdue_now > 5 || (r.oldest_overdue_days || 0) > 3;
                    const compBad = r.completeness_pct != null && r.completeness_pct < 50;
                    return (
                      <tr key={r.id} className="tcRow" style={{ borderTop: '1px solid ' + tk.borderSoft }}>
                        <td style={{ ...s.tdSm, fontWeight: 600 }}>{r.first_name} {(r.last_name || ' ')[0]}.</td>
                        <td style={s.tdSm}>{r.open_leads}</td>
                        <td style={{ ...s.tdSm, fontWeight: 700 }}>
                          {r.overdue_now > 0 ? (
                            <button onClick={() => jumpQueue(r.id)}
                              style={{ ...s.linkNum, color: bad ? tk.danger : tk.warning }}>
                              {r.overdue_now}</button>
                          ) : (
                            <span style={{ color: tk.success }}>{r.overdue_now}</span>
                          )}
                        </td>
                        <td style={{ ...s.tdSm, color: bad ? tk.danger : tk.textSecondary }}>{r.oldest_overdue_days != null ? r.oldest_overdue_days : DASH}</td>
                        <td style={{ ...s.tdSm, fontWeight: 600, color: compBad ? tk.danger : r.completeness_pct >= 80 ? tk.success : tk.textSecondary }}>{r.completeness_pct != null ? r.completeness_pct + '%' : DASH}</td>
                        <td style={s.tdSm}>{r.outcomes_period}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {d.vob_docs && d.vob_docs.length > 0 && (
          <div style={{ ...s.card, marginBottom: 0 }}>
            <div style={{ ...s.cardTitle, display: 'flex', alignItems: 'center', gap: 7 }}><FileCheck2 size={14} color={tk.teal} strokeWidth={1.75} /> VOB DOCUMENTS</div>
            <table style={s.table}>
              <thead><tr>{['Rep', 'VOBs', 'W/ Doc', 'Docs %'].map((h, i) => <th key={i} style={s.th}>{h}</th>)}</tr></thead>
              <tbody>
                {d.vob_docs.map(v => (
                  <React.Fragment key={v.id}>
                    <tr onClick={() => v.missing.length > 0 && setVobOpen(vobOpen === v.id ? null : v.id)} className="tcRow" style={{ borderTop: '1px solid ' + tk.borderSoft, cursor: v.missing.length > 0 ? 'pointer' : 'default' }}>
                      <td style={{ ...s.tdSm, fontWeight: 600 }}>{v.rep}</td>
                      <td style={s.tdSm}>{v.completed}</td>
                      <td style={s.tdSm}>{v.with_docs}</td>
                      <td style={{ ...s.tdSm, fontWeight: 700, color: v.pct >= 100 ? tk.success : v.pct >= 80 ? tk.warning : tk.danger }}>
                        {v.pct != null ? v.pct + '%' : DASH}{v.missing.length > 0 ? ' ' + DOT + ' ' + v.missing.length : ''}
                      </td>
                    </tr>
                    {vobOpen === v.id && v.missing.length > 0 && (
                      <tr>
                        <td colSpan={4} style={{ padding: '2px 8px 8px' }}>
                          <div style={{ fontSize: 12, color: tk.textSecondary }}>
                            <span style={{ fontWeight: 700, color: tk.warning }}>Missing:</span> {v.missing.join(', ')}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            <div style={s.footnote}>Docs % counts toward efficiency scoring.</div>
          </div>
        )}

        {isAdmin && d.rep_sources && d.rep_sources.length > 0 && (
          <div style={{ ...s.card, marginBottom: 0 }}>
            <div style={{ ...s.cardTitle, display: 'flex', alignItems: 'center', gap: 7 }}><TrendingUp size={14} color={tk.purple} strokeWidth={1.75} /> WHERE ADMITS COME FROM</div>
            {d.rep_sources.map(r => {
              const total = r.sources.reduce((a, x) => a + x.admits, 0);
              const top = r.sources[0];
              return (
                <div key={r.rep} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 3 }}>
                    <span style={{ fontWeight: 600, color: tk.ink }}>{r.rep}</span>
                    <span style={{ color: tk.textMuted }}>{top ? top.source : ''} {DOT} {total}</span>
                  </div>
                  <div style={s.barBg}>
                    {top && <div style={{ ...s.bar, width: Math.round(top.admits / total * 100) + '%', background: avColor(r.rep) }} />}
                  </div>
                </div>
              );
            })}
            <div style={s.footnote}>Bar = share from top source. Full mix below.</div>
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <div style={s.card}>
          <div style={s.cardTitle}>PERFORMANCE</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr>{['Rep', 'Admits', 'In Now', 'Discharged', 'Avg LOS', 'Completed', 'AMA/ACA', 'Admin', 'Completion Rate'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const good = r.completion_rate != null && r.completion_rate >= 60;
                  const bad = r.completion_rate != null && r.completion_rate < 40;
                  return (
                    <tr key={r.id} className="tcRow" style={{ borderTop: '1px solid ' + tk.borderSoft }}>
                      <td style={{ ...s.td, fontWeight: 600 }}>{r.first_name} {r.last_name}</td>
                      <td style={s.td}>{r.admits}</td>
                      <td style={s.td}>{r.currently_in}</td>
                      <td style={s.td}>{r.discharged}</td>
                      <td style={{ ...s.td, fontWeight: 600 }}>{r.avg_los ? r.avg_los + ' days' : DASH}</td>
                      <td style={{ ...s.td, color: tk.success, fontWeight: 600 }}>{r.completed}</td>
                      <td style={{ ...s.td, color: r.ama_aca > 0 ? tk.danger : tk.textMuted }}>{r.ama_aca}</td>
                      <td style={s.td}>{r.admin_dc}</td>
                      <td style={{ ...s.td, fontWeight: 700, color: good ? tk.success : bad ? tk.danger : tk.textSecondary }}>
                        {pct(r.completion_rate)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isAdmin && d.revenue && d.revenue.length > 0 && (
        <div style={s.card}>
          <div style={s.cardTitle}>REVENUE COLLECTED {DASH} ADMIN ONLY</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr>{['Rep', 'Admits', 'Collected', 'Per Admit'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {d.revenue.map(r => (
                  <tr key={r.id} className="tcRow" style={{ borderTop: '1px solid ' + tk.borderSoft }}>
                    <td style={{ ...s.td, fontWeight: 600 }}>{r.rep}</td>
                    <td style={s.td}>{r.admits}</td>
                    <td style={{ ...s.td, color: tk.success, fontWeight: 700, fontSize: 15 }}>{money(r.collected)}</td>
                    <td style={s.td}>{money(r.per_admit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {d.by_source.length > 0 && (
        <div style={s.card}>
          <div style={s.cardTitle}>BY LEAD SOURCE</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr>{['Source', 'Admits', 'Avg LOS', 'Completed', 'AMA/ACA'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {d.by_source.map((r, i) => (
                  <tr key={i} className="tcRow" style={{ borderTop: '1px solid ' + tk.borderSoft }}>
                    <td style={{ ...s.td, fontWeight: 600 }}>{r.source}</td>
                    <td style={s.td}>{r.admits}</td>
                    <td style={{ ...s.td, fontWeight: 600 }}>{r.avg_los ? r.avg_los + ' days' : DASH}</td>
                    <td style={{ ...s.td, color: tk.success }}>{r.completed}</td>
                    <td style={{ ...s.td, color: r.ama_aca > 0 ? tk.danger : tk.textMuted }}>{r.ama_aca}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  wrap: { padding: '22px 26px', maxWidth: 1320, margin: '0 auto', fontFamily: tk.font, background: tk.canvas, minHeight: '100vh', boxSizing: 'border-box' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 },
  h1: { fontSize: 25, fontWeight: 700, color: tk.ink, margin: 0, letterSpacing: '-0.02em' },
  sub: { color: tk.textSecondary, margin: '4px 0 0', fontSize: 13 },
  monthChip: { fontSize: 13, fontWeight: 600, color: tk.ink, background: tk.surface, border: '1px solid ' + tk.border, borderRadius: tk.radiusControl, padding: '8px 12px' },
  select: { fontSize: 13, fontWeight: 600, padding: '8px 10px', borderRadius: tk.radiusControl, border: '1px solid ' + tk.border, background: tk.surface, color: tk.ink, cursor: 'pointer', fontFamily: tk.font },
  kpi: { display: 'flex', flexDirection: 'column', gap: 5, padding: '13px 15px', borderRadius: tk.radiusCard, border: '1px solid ' + tk.border, background: tk.surface, boxShadow: tk.shadow },
  kpiIcon: { width: 27, height: 27, borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none' },
  kpiNum: { fontSize: 22, fontWeight: 700, lineHeight: 1.12, color: tk.ink, letterSpacing: '-0.02em' },
  kpiLabel: { fontSize: 10.5, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' },
  protect: { background: tk.surface, border: '1px solid ' + tk.border, borderLeft: '3px solid ' + tk.purple, borderRadius: tk.radiusCard, padding: '12px 16px', marginBottom: 14, boxShadow: tk.shadow },
  card: { background: tk.surface, border: '1px solid ' + tk.border, borderRadius: tk.radiusCard, padding: 16, marginBottom: 14, boxShadow: tk.shadow },
  cardTitle: { fontSize: 11.5, fontWeight: 700, color: tk.textSecondary, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12 },
  howBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '12px 16px', cursor: 'pointer' },
  howBody: { padding: '12px 16px 14px', fontSize: 12.5, color: tk.textSecondary, lineHeight: 1.6, borderTop: '1px solid ' + tk.borderSoft },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { padding: '6px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' },
  td: { padding: '11px 10px', color: tk.ink },
  tdSm: { padding: '7px 8px', color: tk.ink, fontSize: 12.5 },
  avatar: { width: 30, height: 30, borderRadius: 15, color: '#FFFFFF', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none' },
  compCell: { background: tk.surface, border: '1px solid ' + tk.borderSoft, borderRadius: 7, padding: '7px 10px' },
  compLabel: { fontSize: 10, color: tk.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 },
  fixLink: { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, fontWeight: 700, color: tk.ai, fontFamily: tk.font },
  linkNum: { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 700, fontSize: 'inherit', textDecoration: 'underline', fontFamily: tk.font },
  footnote: { fontSize: 11.5, color: tk.textMuted, marginTop: 8 },
  barBg: { height: 5, background: tk.borderSoft, borderRadius: 3 },
  bar: { height: 5, borderRadius: 3 },
  empty: { padding: 60, textAlign: 'center', color: tk.textMuted },
};
